import * as React from "react";
import * as UserStore from "./UserStore";

export interface UserSearchFilters {
    includeStaff: boolean;
    includeStudents: boolean;
    includeParents: boolean;
    includeGuests: boolean;
}

export const useUserSearch = (
    organizationId: string,
    query: string,
    filters: UserSearchFilters,
    limit = 25
) => {
    const [users, setUsers] = React.useState([] as any[]);
    const [isLoading, setIsLoading] = React.useState(false);
    const [error, setError] = React.useState("");
    const [refreshVersion, setRefreshVersion] = React.useState(0);
    const requestVersion = React.useRef(0);

    const refresh = React.useCallback(() => {
        setRefreshVersion((version) => version + 1);
    }, []);

    React.useEffect(() => {
        const version = ++requestVersion.current;

        if (!organizationId) {
            setUsers([]);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError("");

        const timeoutId = window.setTimeout(() => {
            UserStore.SearchUsers(
                query.trim(),
                0,
                limit,
                filters.includeStaff,
                filters.includeStudents,
                filters.includeParents,
                filters.includeGuests
            )
                .then((response) => {
                    if (requestVersion.current !== version) return;
                    setUsers(response.data);
                    setIsLoading(false);
                })
                .catch((request) => {
                    if (requestVersion.current !== version) return;
                    setUsers([]);
                    setError(request.response?.data?.message || "Unable to search users.");
                    setIsLoading(false);
                });
        }, 300);

        return () => {
            window.clearTimeout(timeoutId);
            if (requestVersion.current === version) {
                requestVersion.current += 1;
            }
        };
    }, [
        organizationId,
        query,
        limit,
        filters.includeStaff,
        filters.includeStudents,
        filters.includeParents,
        filters.includeGuests,
        refreshVersion,
    ]);

    return { users, isLoading, error, refresh };
};
